import React, { createContext, useContext, useId } from 'react';

type SwitchProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
};

type GroupContextValue = { labelId: string | undefined };

const GroupContext = createContext<GroupContextValue>({ labelId: undefined });

const Switch = ({
  checked,
  onChange,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: SwitchProps) => {
  const { labelId } = useContext(GroupContext);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-label={!labelId ? ariaLabel : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === ' ') {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      className={[
        'relative inline-flex h-6 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-green-500' : 'bg-gray-300',
        className ?? '',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'pointer-events-none inline-block h-5 w-5 rounded-full bg-green-100 shadow-md ring-0 transition-transform duration-200 ease-in-out',
          checked ? 'translate-x-5' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  );
};

const Group = ({ children }: { children: React.ReactNode }) => {
  const id = useId();
  const labelId = `switch-label-${id}`;
  return (
    <GroupContext.Provider value={{ labelId }}>
      <div className="flex items-center gap-3">{children}</div>
    </GroupContext.Provider>
  );
};

const Label = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  const { labelId } = useContext(GroupContext);
  return (
    <span id={labelId} className={['text-sm text-gray-700', className ?? ''].join(' ')}>
      {children}
    </span>
  );
};

Switch.Group = Group;
Switch.Label = Label;

export default Switch;
